import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const BEFORE = "https://static.prod-images.emergentagent.com/jobs/c74574e6-69b0-4d56-80d7-aa66fb56a651/images/aaf79e0db23b55c1b52b07d9cafead7f8fcac80e72adf2fc22121383fc25e2b8.jpeg";
const AFTER = "https://static.prod-images.emergentagent.com/jobs/c74574e6-69b0-4d56-80d7-aa66fb56a651/images/16460015d921e8886f99644345692bb22a69e4c189b6bcdb290b8f3b80b4023c.jpeg";

export const BeforeAfter = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);
  const { t } = useI18n();

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(2, Math.min(98, p)));
  }, []);

  return (
    <section id="beforeafter" data-testid="beforeafter-section" className="relative border-t border-white/10 bg-ink-900">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ • ]">{t("ba.label")}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("ba.h1")} <span className="text-aurora">{t("ba.hkw")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">
              {t("ba.sub")}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <div
            ref={ref}
            data-testid="ba-slider"
            className="relative mt-12 aspect-[16/10] w-full cursor-ew-resize select-none overflow-hidden rounded-2xl border border-white/10 sm:aspect-[16/8]"
            onPointerDown={(e) => { dragging.current = true; update(e.clientX); }}
            onPointerMove={(e) => { if (dragging.current) update(e.clientX); }}
            onPointerUp={() => { dragging.current = false; }}
            onPointerLeave={() => { dragging.current = false; }}
          >
            {/* AFTER (base) */}
            <img src={AFTER} alt="Infrastructură administrată de DMO" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
            <span className="pointer-events-none absolute bottom-4 right-4 rounded-full border border-cyan-neon/40 bg-ink-900/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-cyan-neon backdrop-blur-md">
              {t("ba.after")}
            </span>

            {/* BEFORE (clipped) */}
            <img src={BEFORE} alt="Infrastructură haotică" className="pointer-events-none absolute inset-0 h-full w-full object-cover" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
            <span className="pointer-events-none absolute bottom-4 left-4 rounded-full border border-[#ff3b6b]/40 bg-ink-900/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[#ff7a97] backdrop-blur-md" style={{ opacity: pos > 12 ? 1 : 0 }}>
              {t("ba.before")}
            </span>

            {/* Handle */}
            <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%` }}>
              <div className="absolute inset-y-0 -left-px w-0.5 bg-cyan-neon shadow-[0_0_16px_rgba(0,229,255,0.7)]" />
              <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full border border-cyan-neon bg-ink-900/90 text-cyan-neon backdrop-blur-md">
                <MoveHorizontal size={18} />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
